import React, { useEffect } from "react";
import styles from "../QuoteMultipleDestinationsPanel.module.css";
import { FreightTruck } from "../../../types/IQuotes";
import { useNitroCAStore } from "../../../store/CompetitiveAnalysisStore";
import { useAnchorPosition } from "./multiLocationQuoteUtils";
import { icons } from "../../../svg";

function useOnClickOutside(
  ref: React.RefObject<HTMLDivElement>,
  onOutside: (event: PointerEvent) => void,
  isOpen: boolean,
) {
  useEffect(() => {
    if (!isOpen) return;

    const handler = (event: PointerEvent) => {
      const panel = ref.current;
      if (!panel) return;

      const path = event.composedPath() ?? [];
      const clickedInside = path.length
        ? path.includes(panel)
        : panel.contains(event.target as Node);

      if (!clickedInside) {
        onOutside(event);
      }
    };

    document.addEventListener("pointerdown", handler, { capture: true });

    // cleanup function runs when component unmounts
    return () => {
      document.removeEventListener("pointerdown", handler, { capture: true });
    };
  }, [ref, onOutside, isOpen]);
}

interface TruckFreightDetailPanelProps extends FreightTruck {
  isMobile?: boolean;
}

const TruckFreightDetailPanel: React.FC<TruckFreightDetailPanelProps> = (
  props,
) => {
  const {
    totalMiles,
    costPerMile,
    rate,
    fsc,
    fscRate,
    isMobile = false,
  } = props;
  const [
    setDisplayTruckFreightDetailsPanel,
    displayTruckFreightDetailsPanel,
    anchorForTruckDetailsPanel,
  ] = useNitroCAStore((state) => [
    state.setDisplayTruckFreightDetailsPanel,
    state.displayTruckFreightDetailsPanel,
    state.anchorForTruckDetailsPanel,
  ]);
  const ref = React.useRef<HTMLDivElement>(null);
  useOnClickOutside(
    ref,
    () => {
      setDisplayTruckFreightDetailsPanel(false);
    },
    displayTruckFreightDetailsPanel,
  );
  const anchorPosition = useAnchorPosition(anchorForTruckDetailsPanel);
  return (
    <div
      ref={ref}
      className={`${styles.truckFreightDetailsContainer} ${
        isMobile ? styles.truckFreightDetailsMobile : ""
      }`}
      style={
        isMobile
          ? {}
          : {
              top: anchorPosition.top - 60,
              left: anchorPosition.left + 20,
            }
      }
    >
      {/* Title */}
      <div
        className={`${styles.truckFreightDetailsTitle} ${
          isMobile ? styles.truckFreightDetailsTitleMobile : ""
        }`}
      >
        <span
          className={isMobile ? styles.truckFreightDetailsTitleTextMobile : ""}
        >
          Truck Freight Details
        </span>
        {isMobile && (
          <button
            type="button"
            onClick={() => setDisplayTruckFreightDetailsPanel(false)}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
            }}
          >
            <img
              id="mobile-circle-close-icon"
              src={icons.CircleCloseIcon}
              alt="Circle Close Icon"
            />
          </button>
        )}
      </div>
      {/* Content */}
      <div className={styles.truckFreightDetailsContent}>
        <div className={styles.truckFreightTextContainer}>
          <span
            className={
              isMobile
                ? styles.truckFreightKeyTextMobile
                : styles.truckFreightKeyText
            }
          >
            Miles:
          </span>
          <span
            className={
              isMobile
                ? styles.truckFreightValueTextMobile
                : styles.truckFreightValueText
            }
          >
            {totalMiles}mi
          </span>
        </div>
        <div className={styles.truckFreightTextContainer}>
          <span
            className={
              isMobile
                ? styles.truckFreightKeyTextMobile
                : styles.truckFreightKeyText
            }
          >
            $/mi:
          </span>
          <span
            className={
              isMobile
                ? styles.truckFreightValueTextMobile
                : styles.truckFreightValueText
            }
          >
            ${costPerMile.toFixed(2)}
          </span>
        </div>
        <div className={styles.truckFreightTextContainer}>
          <span
            className={
              isMobile
                ? styles.truckFreightKeyTextMobile
                : styles.truckFreightKeyText
            }
          >
            Rate:
          </span>
          <span
            className={
              isMobile
                ? styles.truckFreightValueTextMobile
                : styles.truckFreightValueText
            }
          >
            ${rate} / ST
          </span>
        </div>
        <div className={styles.truckFreightTextContainer}>
          <span
            className={
              isMobile
                ? styles.truckFreightKeyTextMobile
                : styles.truckFreightKeyText
            }
          >
            Fuel:
          </span>
          <span
            className={
              isMobile
                ? styles.truckFreightValueTextMobile
                : styles.truckFreightValueText
            }
          >
            {fscRate}%
          </span>
        </div>
        <div className={styles.truckFreightTextContainer}>
          <span
            className={
              isMobile
                ? styles.truckFreightKeyTextMobile
                : styles.truckFreightKeyText
            }
          >
            FSC:
          </span>
          <span
            className={
              isMobile
                ? styles.truckFreightValueTextMobile
                : styles.truckFreightValueText
            }
          >
            ${fsc.toFixed(2)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default TruckFreightDetailPanel;
